import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { TrendingUp, Clock, IndianRupee, FileText, CheckCircle2, AlertTriangle, Building2, Plus } from 'lucide-react';
import InvoiceBuilderModal, { InvoiceList } from '@/components/InvoiceBuilderModal';

const currency = v => `\u20B9${Number(v || 0).toLocaleString('en-IN')}`;

export default function VendorFinancePage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const loadInvoices = () => api.get('/invoices/mine').then(r => setInvoices(r.data || [])).catch(() => setInvoices([]));

  useEffect(() => {
    api.get('/vendor/my-dashboard').then(r => setData(r.data)).catch(() => {});
    loadInvoices();
  }, [api]);

  if (!data) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-zinc-600 text-sm">Loading...</div></div>;

  const vendor = data.vendor;
  const tasks = data.tasks?.items || [];
  const workOrders = data.work_orders || [];
  const acceptedWOs = workOrders.filter(wo => wo.status === 'accepted');
  const issuedWOs = workOrders.filter(wo => wo.status === 'issued');
  const allWOs = [...acceptedWOs, ...issuedWOs];

  // Financial totals from accepted Work Orders
  const totalWOValue = acceptedWOs.reduce((s, wo) => s + (wo.total_amount || 0), 0);
  const pendingWOValue = issuedWOs.reduce((s, wo) => s + (wo.total_amount || 0), 0);

  // Task-based earnings
  const completed = tasks.filter(t => t.status === 'completed');
  const totalEarned = completed.reduce((s, t) => s + (t.rate_amount || 0), 0);
  const totalAssigned = tasks.reduce((s, t) => s + (t.rate_amount || 0), 0);

  // Milestone calculations from accepted WOs
  const totalAdvance = acceptedWOs.reduce((s, wo) => {
    const milestones = wo.payment_terms?.milestones || [];
    const adv = milestones.find(m => m.name === 'Advance');
    return s + ((wo.total_amount || 0) * (adv?.percent || 0) / 100);
  }, 0);

  return (
    <div className="space-y-5" data-testid="vendor-finance">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Finance & Payments</h1>
          <p className="text-xs text-zinc-500 mt-1">{vendor?.company_name || vendor?.full_name} — Financial overview from accepted Work Orders</p>
        </div>
        <button onClick={() => { setEditingInvoice(null); setShowBuilder(true); }} data-testid="raise-invoice-btn"
          disabled={acceptedWOs.length === 0}
          className="h-10 px-5 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-950 text-xs font-bold rounded-md flex items-center gap-2 transition-colors">
          <Plus className="w-4 h-4" /> Raise Invoice
        </button>
      </div>

      {acceptedWOs.length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-3 flex items-start gap-2 text-xs text-amber-300">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>You need at least one <strong>accepted</strong> Work Order to raise an invoice. Accept any pending WOs first.</span>
        </div>
      )}

      {/* Top Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-zinc-900 border border-emerald-500/20 rounded-md p-4">
          <TrendingUp className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="text-xl font-heading font-bold text-emerald-400">{currency(totalWOValue)}</p>
          <p className="text-[10px] text-zinc-500">Accepted WO Value</p>
        </div>
        <div className="bg-zinc-900 border border-amber-500/20 rounded-md p-4">
          <Clock className="w-5 h-5 text-amber-400 mb-2" />
          <p className="text-xl font-heading font-bold text-amber-400">{currency(pendingWOValue)}</p>
          <p className="text-[10px] text-zinc-500">Pending Acceptance</p>
        </div>
        <div className="bg-zinc-900 border border-cyan-500/20 rounded-md p-4">
          <IndianRupee className="w-5 h-5 text-cyan-400 mb-2" />
          <p className="text-xl font-heading font-bold text-cyan-400">{currency(totalAdvance)}</p>
          <p className="text-[10px] text-zinc-500">Advance Due</p>
        </div>
        <div className="bg-zinc-900 border border-blue-500/20 rounded-md p-4">
          <CheckCircle2 className="w-5 h-5 text-blue-400 mb-2" />
          <p className="text-xl font-heading font-bold text-blue-400">{currency(totalEarned)}</p>
          <p className="text-[10px] text-zinc-500">Work Completed</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
          <FileText className="w-5 h-5 text-zinc-400 mb-2" />
          <p className="text-xl font-heading font-bold text-white">{acceptedWOs.length}</p>
          <p className="text-[10px] text-zinc-500">Active Work Orders</p>
        </div>
      </div>

      {/* Accepted Work Orders */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Work Order Financials</h3>
          <span className="text-[10px] text-zinc-500">{allWOs.length} work orders</span>
        </div>
        {allWOs.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <FileText className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
            <p className="text-xs text-zinc-500">No work orders yet</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/30">
            {allWOs.map(wo => {
              const milestones = wo.payment_terms?.milestones || [];
              return (
                <div key={wo.id} className="px-4 py-3" data-testid={`finance-wo-${wo.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white font-semibold">{wo.work_order_no}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                          wo.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>{wo.status === 'accepted' ? 'Accepted' : 'Pending'}</span>
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-0.5">{wo.project_name} &middot; {wo.scope_title}</p>
                    </div>
                    <span className="text-sm font-mono text-amber-400 font-bold">{currency(wo.total_amount)}</span>
                  </div>
                  {milestones.length > 0 && (
                    <div className="flex gap-3 mt-2">
                      {milestones.map((m, i) => (
                        <div key={i} className="text-[9px] bg-zinc-950 border border-zinc-800 rounded px-2.5 py-1.5">
                          <span className="text-zinc-500">{m.name}:</span>
                          <span className="text-zinc-300 ml-1">{m.percent}%</span>
                          <span className="text-emerald-400 ml-1 font-mono">{currency((wo.total_amount || 0) * (m.percent || 0) / 100)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invoices section */}
      <InvoiceList
        invoices={invoices}
        onEdit={(inv) => { setEditingInvoice(inv); setShowBuilder(true); }}
        onDelete={async (inv) => {
          if (!window.confirm(`Delete draft invoice ${inv.invoice_no}?`)) return;
          try { await api.delete(`/invoices/${inv.id}`); await loadInvoices(); } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
        }}
      />

      {/* Contract Details */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
        <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">Contract & Banking</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
          <div><p className="text-[9px] text-zinc-600 uppercase">Pricing Model</p><p className="text-xs text-zinc-300 capitalize">{vendor?.pricing_model?.replace(/_/g,' ') || 'Per Shot'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">Payment Terms</p><p className="text-xs text-zinc-300 capitalize">{vendor?.payment_terms?.replace(/_/g,' ') || 'Milestone'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">Entity</p><p className="text-xs text-zinc-300 capitalize">{vendor?.entity_type}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">NDA</p><p className={`text-xs ${vendor?.nda_signed ? 'text-emerald-400' : 'text-red-400'}`}>{vendor?.nda_signed ? 'Signed' : 'Not Signed'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">Bank</p><p className="text-xs text-zinc-300">{vendor?.bank_name || '-'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">Account</p><p className="text-xs text-zinc-300 font-mono">{vendor?.bank_account_number ? `XXXX${vendor.bank_account_number.slice(-4)}` : '-'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">IFSC</p><p className="text-xs text-zinc-300 font-mono">{vendor?.bank_ifsc || '-'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">GST</p><p className="text-xs text-zinc-300 font-mono">{vendor?.gst_number || '-'}</p></div>
        </div>
      </div>

      {/* Completed Work */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h3 className="text-xs font-semibold text-zinc-300 uppercase">Completed Work ({completed.length} tasks) &middot; {currency(totalEarned)}</h3>
        </div>
        <div className="divide-y divide-zinc-800/30 max-h-60 overflow-y-auto">
          {completed.map(t => (
            <div key={t.id} className="px-4 py-2.5 flex items-center justify-between">
              <div><p className="text-xs text-zinc-300">{t.name}</p><p className="text-[10px] text-zinc-600">{t.task_type}</p></div>
              <span className="text-xs font-mono text-emerald-400">{currency(t.rate_amount)}</span>
            </div>
          ))}
          {completed.length === 0 && <p className="text-xs text-zinc-600 text-center py-6">No completed work yet</p>}
        </div>
      </div>

      {/* Invoice Builder Modal */}
      <InvoiceBuilderModal
        open={showBuilder}
        onClose={() => { setShowBuilder(false); setEditingInvoice(null); }}
        workOrders={acceptedWOs}
        editingInvoice={editingInvoice}
        onCreated={() => { loadInvoices(); }}
      />
    </div>
  );
}
